import { createContext, useContext } from "react";
import type {
  RelatedAudiencesQuery,
  QueryBuilderJourneysQuery,
  RelationshipWithoutFilterableColumnsFragment,
  TraitDefinitionWithoutFilterableColumnsFragment,
} from "src/graphql";
import {
  type FilterableColumn,
  initialPropertyCondition,
} from "src/types/visual";
import type { FilterOption } from "src/components/explore/filter-popover/constants";
import type { BaseParentModel } from "src/components/audiences/types";
import type { IconBoxProps } from "@hightouch/htui";
import type { ConditionBuilder } from "./build-condition-from-column";

// TODO(nolan): extend similar interface from constants
export interface FilterableSection {
  type: FilterOption;
  label: string;
  icon: IconBoxProps["icon"];
  shade: IconBoxProps["color"];
  iconVariant?: IconBoxProps["variant"];
  options?: Record<string, any>;
  placeholder?: string;
  overrideColumnIcon?: boolean;
  tip?: string;
  separator?: boolean;
  heading?: string;
}

export interface FilterableOptionsData {
  isLoading: boolean;
  /** True while a modelId is set but the parent model query has not resolved yet. */
  isParentModelLoading: boolean;
  modelId?: string | null;
  modelName?: string | null;
  columns: FilterableColumn[];
  events: RelationshipWithoutFilterableColumnsFragment[];
  messageEvents: RelationshipWithoutFilterableColumnsFragment[];
  relationships: RelationshipWithoutFilterableColumnsFragment[];
  traits: (TraitDefinitionWithoutFilterableColumnsFragment & {
    parentModel?: BaseParentModel | null;
  })[];
  /**
   * All trait definitions keyed by id — unfiltered, unlike `traits`, so save
   * validation can resolve references to traits the picker hides. Undefined
   * until the trait query resolves.
   */
  traitsById?: ReadonlyMap<
    string,
    TraitDefinitionWithoutFilterableColumnsFragment
  >;
  relatedAudiences: RelatedAudiencesQuery["segments"];
  /** Stable existence signal, not the live search result — see useFilterableOptions. */
  hasReferenceableAudiences: boolean;
  parentSnapshottingEnabled: boolean;
  parentConnectionId: string | null | undefined;
  journeys: QueryBuilderJourneysQuery["journeys"];
  filterableColumnsByModelId: Record<string, FilterableColumn[]>;
  managedColumnNames: Set<string>;
}

export interface FilterableOptionsContextValue extends FilterableOptionsData {
  nestingLevel: number;
  sections: FilterableSection[];
  buildCondition: ConditionBuilder;

  /** The type of the filter who is creating this context (i.e. filter who owns the model id) */
  parentFilterOptionType?:
    | FilterOption.Event
    | FilterOption.RealtimeEvent
    | FilterOption.Relation
    | FilterOption.MessageEvent;

  /** Separate from `parentFilterOptionType` because managed models reached as a plain relation (e.g. subscription topics) are not `FilterOption.MessageEvent` */
  isCepManagedModel?: boolean;

  /** Icon override for property columns when the parent is a message event (channel-specific icon) */
  parentIconOverride?: {
    icon: IconBoxProps["icon"];
    color: IconBoxProps["color"];
    variant?: IconBoxProps["variant"];
  };

  /** The semantic type of the related model (e.g. "people", "households", "accounts") */
  semanticType?: string | null;

  /** Stores all the filterable option contexts up to the root. Index is 0 and last index is immediate parent. */
  parents: FilterableOptionsData[];

  /** Update the search string driving the server-side audience search query. */
  setAudienceSearch: (s: string) => void;
}

const defaultFilterableOptionsContextValue: FilterableOptionsContextValue = {
  nestingLevel: -1,
  sections: [],
  buildCondition: () => initialPropertyCondition,
  parents: [],
  isLoading: true,
  isParentModelLoading: false,
  columns: [],
  relationships: [],
  traits: [],
  events: [],
  messageEvents: [],
  relatedAudiences: [],
  hasReferenceableAudiences: false,
  parentSnapshottingEnabled: false,
  parentConnectionId: undefined,
  journeys: [],
  filterableColumnsByModelId: {},
  managedColumnNames: new Set(),
  setAudienceSearch: () => {},
};

export const FilterableOptionsContext =
  createContext<FilterableOptionsContextValue>(
    defaultFilterableOptionsContextValue,
  );

/**
 * Hook to consume the nearest filterable options context.
 * A FilterPopover will use this to get its data and configuration.
 */
export const useFilterableOptionsContext = () => {
  const context = useContext(FilterableOptionsContext);
  return context;
};
